import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  useMediaQuery,
  useTheme,
  Container,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navigationItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Services', path: '/services' },
    { label: 'Resource Center', path: '/resources' },
    { label: 'Contact', path: '/contact' },
    { label: 'Locations', path: '/locations' },
  ];

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  // Logo component matching the described design
  const LogoComponent = () => (
    <Box
      component={Link}
      to="/"
      sx={{
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': {
          opacity: 0.9,
        },
      }}
    >
      {/* House roof icon from logo */}
      <Box
        component="img"
        src="/madtitle.png"
        alt="M.A.D. Title Agency"
        title="M.A.D. Title Agency logo"
        sx={{
          height: { xs: '32px', md: '40px' },
          width: 'auto',
          mr: 1,
        }}
      />
    </Box>
  );

  // Navigation buttons for desktop
  const NavigationButtons = () => (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
      {navigationItems.map((item) => (
        <Button
          key={item.label}
          component={Link}
          to={item.path}
          sx={{
            color: isActiveRoute(item.path) ? '#1e3a8a' : '#64748b',
            fontWeight: isActiveRoute(item.path) ? 700 : 600,
            fontSize: '0.875rem',
            textTransform: 'none',
            letterSpacing: '0.02em',
            px: 2,
            py: 1,
            borderRadius: 1,
            position: 'relative',
            '&:hover': {
              backgroundColor: 'rgba(30, 58, 138, 0.04)',
              color: '#1e3a8a',
            },
            // Active state indicator
            '&::after': isActiveRoute(item.path) ? {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '24px',
              height: '3px',
              backgroundColor: '#f59e0b', // Gold accent
              borderRadius: '2px',
            } : {},
          }}
        >
          {item.label}
        </Button>
      ))}
    </Box>
  );

  // Mobile drawer content
  const DrawerContent = () => (
    <Box
      sx={{
        width: 280,
        height: '100%',
        backgroundColor: '#ffffff',
        pt: 2,
      }}
    >
      {/* Logo in drawer header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pb: 2,
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
        }}
      >
        <LogoComponent />
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            color: '#4b5563',
            '&:hover': {
              backgroundColor: 'rgba(100, 116, 139, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Navigation items */}
      <List sx={{ pt: 2 }}>
        {navigationItems.map((item) => (
          <ListItem
            key={item.label}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              color: isActiveRoute(item.path) ? '#1e3a8a' : '#4b5563',
              backgroundColor: isActiveRoute(item.path) 
                ? 'rgba(30, 58, 138, 0.04)' 
                : 'transparent',
              borderRight: isActiveRoute(item.path) 
                ? '4px solid #f59e0b' 
                : '4px solid transparent',
              mb: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(30, 58, 138, 0.04)',
                color: '#1e3a8a',
              },
            }}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: isActiveRoute(item.path) ? 700 : 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                letterSpacing: '0.02em',
              }}
            />
          </ListItem>
        ))}
      </List>

      {/* Contact info in drawer footer */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          backgroundColor: '#f9fafb',
          borderTop: '1px solid rgba(226, 232, 240, 0.8)',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: '#4b5563',
            textAlign: 'center',
            mb: 1,
          }}
        >
          Get Started Today
        </Typography>
        <Button
          variant="contained"
          fullWidth
          component={Link}
          to="/contact"
          onClick={handleDrawerToggle}
          sx={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            fontSize: '0.75rem',
            py: 1.5,
            '&:active': {
              transform: 'scale(0.98)',
              boxShadow: '0 2px 6px rgba(30, 58, 138, 0.25)',
            },
          }}
        >
          FREE QUOTE
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            px: { xs: 2, md: 4 },
            py: 1,
          }}
        >
          {/* Logo */}
          <LogoComponent />

          {/* Desktop Navigation */}
          <NavigationButtons />

          {/* CTA Button (Desktop) */}
          <Button
            variant="contained"
            component={Link}
            to="/contact"
            sx={{
              display: { xs: 'none', md: 'flex' },
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              px: 3,
              py: 1,
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              boxShadow: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
                boxShadow: '0 4px 8px rgba(30, 58, 138, 0.2)',
              },
            }}
          >
            FREE QUOTE
          </Button>

          {/* Mobile Menu Button */}
          <IconButton
            edge="end"
            onClick={handleDrawerToggle}
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: '#4b5563',
              '&:hover': {
                backgroundColor: 'rgba(100, 116, 139, 0.1)',
              },
              '&:focus-visible': {
                outline: '2px solid #1e3a8a',
                outlineOffset: '2px',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
          },
        }}
      >
        <DrawerContent />
      </Drawer>
    </>
  );
};

export default Header; 